/**
 * Support page — per docs/specs/support.md
 *
 * Multi-channel ticket queue. Row click opens the ticket detail panel
 * (conversation thread + context + composer). URL params back segment,
 * priority, channel, search.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-20-v3
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Bot, Mail, MessageCircle, Phone, Search, Smartphone } from 'lucide-react';
import { Chip } from '../../components/chip';
import { Modal } from '../../components/modal';
import { RoleButton } from '../../components/role-gate';
import { useToast } from '../../components/toast';
import { useShell } from '../../layout/shell';
import { useSupportKpis, useSupportTickets } from '../../../lib/api';
import { useAdminSession } from '../../../lib/auth';
import { apiPost, getHttpErrorMessage } from '../../../lib/http';
import type {
    SupportTicket,
    TicketChannel,
    TicketPriority,
    TicketStatus
} from '../../../lib/types';
import { TicketDetailPanel } from './ticket-detail-panel';

const SEGMENTS: { id: string; label: string }[] = [
    { id: 'open', label: 'Open' },
    { id: 'waiting', label: 'Waiting customer' },
    { id: 'breached', label: 'Breached SLA' },
    { id: 'mine', label: 'My tickets' },
    { id: 'unassigned', label: 'Unassigned' },
    { id: 'resolved', label: 'Resolved (7d)' },
    { id: 'all', label: 'All' }
];

const NEW_TICKET_DEFAULTS = {
    customer_email: '',
    category: 'other',
    channel: 'email' as TicketChannel,
    summary: '',
    user_message: ''
};

const priorityTone = (p: TicketPriority) =>
    p === 'urgent' ? 'danger' : p === 'high' ? 'warning' : p === 'normal' ? 'info' : 'muted';

const statusTone = (s: TicketStatus) => {
    if (s === 'open') return 'info';
    if (s === 'in_progress') return 'purple';
    if (s === 'waiting_customer') return 'warning';
    if (s === 'waiting_internal') return 'muted';
    if (s === 'resolved' || s === 'closed') return 'success';
    return 'muted';
};

function ChannelIcon({ channel }: { channel: TicketChannel }) {
    if (channel === 'help_ai') return <Bot size={12} />;
    if (channel === 'email') return <Mail size={12} />;
    if (channel === 'chat') return <MessageCircle size={12} />;
    if (channel === 'sms') return <Smartphone size={12} />;
    return <Phone size={12} />;
}

export function SupportPage() {
    const [params, setParams] = useSearchParams();
    const segment = params.get('segment') ?? 'open';
    const priority = params.get('priority') ?? 'all';
    const channel = params.get('channel') ?? 'all';
    const q = params.get('q') ?? '';
    const openId = params.get('ticket');

    const [searchInput, setSearchInput] = useState(q);
    const [newTicketOpen, setNewTicketOpen] = useState(false);
    const [newTicketBusy, setNewTicketBusy] = useState(false);
    const [newTicket, setNewTicket] = useState(NEW_TICKET_DEFAULTS);
    useEffect(() => setSearchInput(q), [q]);

    const queryClient = useQueryClient();
    const kpis = useSupportKpis();
    const tickets = useSupportTickets();
    const session = useAdminSession();

    const { setDetailPanel } = useShell();
    const { toast } = useToast();

    function updateParam(key: string, value: string | null) {
        const next = new URLSearchParams(params);
        if (!value || value === 'all' || (key === 'segment' && value === 'open')) next.delete(key);
        else next.set(key, value);
        setParams(next, { replace: true });
    }

    function updateNewTicket(key: keyof typeof NEW_TICKET_DEFAULTS, value: string) {
        setNewTicket((current) => ({ ...current, [key]: value }));
    }

    function closeNewTicket() {
        if (newTicketBusy) return;
        setNewTicketOpen(false);
        setNewTicket(NEW_TICKET_DEFAULTS);
    }

    async function submitNewTicket() {
        if (
            !newTicket.customer_email.trim() ||
            !newTicket.summary.trim() ||
            !newTicket.user_message.trim()
        ) {
            toast('Missing ticket details', {
                kind: 'danger',
                detail: 'Customer email, subject, and first message are required.'
            });
            return;
        }

        setNewTicketBusy(true);
        try {
            const created = await apiPost<SupportTicket>('/api/admin/support/tickets', newTicket);
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'tickets'] }),
                queryClient.invalidateQueries({ queryKey: ['admin', 'support', 'kpis'] })
            ]);
            toast('Support ticket created', {
                detail: `#PD-${created.number} · ${created.customer_email}`
            });
            setNewTicketOpen(false);
            setNewTicket(NEW_TICKET_DEFAULTS);
            updateParam('ticket', created.id);
        } catch (error) {
            toast('Failed to create ticket', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Ticket was not created')
            });
        } finally {
            setNewTicketBusy(false);
        }
    }

    useEffect(() => {
        if (openId) {
            setDetailPanel(
                <TicketDetailPanel ticketId={openId} onClose={() => updateParam('ticket', null)} />
            );
        } else {
            setDetailPanel(null);
        }
        return () => setDetailPanel(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openId, setDetailPanel]);

    const rows = (tickets.data ?? []).filter((t) => {
        if (segment === 'open' && !['open', 'in_progress', 'reopened'].includes(t.status))
            return false;
        if (segment === 'waiting' && t.status !== 'waiting_customer') return false;
        if (segment === 'breached' && !t.sla_breaches) return false;
        if (segment === 'mine' && t.assignee !== session.name) return false;
        if (segment === 'unassigned' && t.assignee) return false;
        if (segment === 'resolved' && !['resolved', 'closed'].includes(t.status)) return false;
        if (priority !== 'all' && t.priority !== priority) return false;
        if (channel !== 'all' && !t.active_channels.includes(channel as TicketChannel))
            return false;
        if (q.trim()) {
            const s = q.toLowerCase();
            return (
                String(t.number).includes(s) ||
                t.subject.toLowerCase().includes(s) ||
                t.customer_email.toLowerCase().includes(s)
            );
        }
        return true;
    });

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Support</h1>
                    <p>Multi-channel tickets triaged by Glimbr AI and humans.</p>
                </div>
                <div className="page-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() =>
                            toast('Open any ticket to insert canned replies into the composer', {
                                kind: 'info'
                            })
                        }
                    >
                        Canned replies
                    </button>
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-primary"
                        type="button"
                        onClick={() => setNewTicketOpen(true)}
                    >
                        New ticket
                    </RoleButton>
                </div>
            </header>

            <section className="kpi-grid kpi-grid-8" aria-label="Support metrics">
                <KpiMini label="Open" value={kpis.data?.open.toString() ?? '—'} />
                <KpiMini label="Avg first response" value={kpis.data?.avg_first_response ?? '—'} />
                <KpiMini label="Avg resolution" value={kpis.data?.avg_resolution ?? '—'} />
                <KpiMini
                    label="CSAT"
                    value={kpis.data?.csat != null ? `${kpis.data.csat.toFixed(1)} / 5` : '—'}
                />
                <KpiMini
                    label="AI first-contact"
                    value={
                        kpis.data?.ai_fcr_rate != null
                            ? `${kpis.data.ai_fcr_rate.toFixed(1)}%`
                            : '—'
                    }
                />
                <KpiMini
                    label="Breaching SLA"
                    value={kpis.data?.breaching_sla.toString() ?? '—'}
                    alert={!!kpis.data?.breaching_sla}
                />
                <KpiMini label="Unassigned" value={kpis.data?.unassigned.toString() ?? '—'} />
                <KpiMini
                    label="> 7d backlog"
                    value={kpis.data?.backlog_over_7d.toString() ?? '—'}
                />
            </section>

            <div className="segment-tabs" role="tablist" aria-label="Ticket segment">
                {SEGMENTS.map((s) => (
                    <button
                        key={s.id}
                        role="tab"
                        aria-selected={segment === s.id}
                        onClick={() => updateParam('segment', s.id)}
                        className="segment-tab"
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="filter-bar">
                <label className="search">
                    <Search size={13} />
                    <input
                        type="search"
                        placeholder="Search by ticket #, email, subject"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') updateParam('q', searchInput);
                        }}
                        onBlur={() => updateParam('q', searchInput)}
                    />
                </label>
                <select
                    className="select"
                    value={priority}
                    onChange={(e) => updateParam('priority', e.target.value)}
                    aria-label="Priority"
                >
                    <option value="all">Any priority</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="normal">Normal</option>
                    <option value="low">Low</option>
                </select>
                <select
                    className="select"
                    value={channel}
                    onChange={(e) => updateParam('channel', e.target.value)}
                    aria-label="Channel"
                >
                    <option value="all">Any channel</option>
                    <option value="email">Email</option>
                    <option value="chat">Chat</option>
                    <option value="help_ai">Help AI</option>
                    <option value="sms">SMS</option>
                    <option value="phone">Phone</option>
                </select>
                <span className="count">{rows.length} tickets</span>
            </div>

            <div className="card">
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Customer · subject</th>
                                <th>Channels</th>
                                <th>Priority</th>
                                <th>Status</th>
                                <th>Assignee</th>
                                <th>Age</th>
                                <th>SLA</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((t) => (
                                <tr
                                    key={t.id}
                                    aria-selected={openId === t.id}
                                    onClick={() => updateParam('ticket', t.id)}
                                >
                                    <td className="mono">#PD-{t.number}</td>
                                    <td>
                                        <div className="user-cell">
                                            <div className="avatar">
                                                {(t.customer_name ?? t.customer_email)
                                                    .split(/[\s.@]/)
                                                    .filter(Boolean)
                                                    .map((w) => w[0])
                                                    .slice(0, 2)
                                                    .join('')
                                                    .toUpperCase()}
                                            </div>
                                            <div className="user-meta">
                                                <span className="user-name">
                                                    {t.customer_name ?? t.customer_email}
                                                    {t.ai_handled && (
                                                        <span
                                                            className="chip"
                                                            data-tone="purple"
                                                            style={{
                                                                marginLeft: 6,
                                                                height: 16,
                                                                fontSize: 9
                                                            }}
                                                        >
                                                            AI
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="user-sub">{t.subject}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            {t.active_channels.map((c) => (
                                                <span
                                                    key={c}
                                                    title={c}
                                                    style={{
                                                        color: 'var(--text-muted)',
                                                        display: 'inline-flex'
                                                    }}
                                                >
                                                    <ChannelIcon channel={c} />
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <Chip tone={priorityTone(t.priority)}>{t.priority}</Chip>
                                    </td>
                                    <td>
                                        <Chip tone={statusTone(t.status)} dot>
                                            {t.status.replace('_', ' ')}
                                        </Chip>
                                    </td>
                                    <td className="text-muted">{t.assignee ?? '—'}</td>
                                    <td className="text-muted">{t.age}</td>
                                    <td>
                                        <Chip tone={t.sla_breaches ? 'danger' : 'muted'}>
                                            {t.sla_countdown}
                                        </Chip>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal
                open={newTicketOpen}
                title="New support ticket"
                subtitle="Create a ticket against an existing Producer Dashboard user."
                onClose={closeNewTicket}
                width={620}
                footer={
                    <>
                        <button
                            className="btn btn-ghost"
                            type="button"
                            onClick={closeNewTicket}
                            disabled={newTicketBusy}
                        >
                            Cancel
                        </button>
                        <RoleButton
                            scope="support_ok"
                            className="btn btn-primary"
                            type="button"
                            onClick={submitNewTicket}
                            disabled={newTicketBusy}
                        >
                            {newTicketBusy ? 'Creating…' : 'Create ticket'}
                        </RoleButton>
                    </>
                }
            >
                <div className="stack-12">
                    <div className="form-field">
                        <label className="form-label" htmlFor="support-customer-email">
                            Customer email
                        </label>
                        <input
                            id="support-customer-email"
                            className="form-input"
                            type="email"
                            value={newTicket.customer_email}
                            onChange={(event) =>
                                updateNewTicket('customer_email', event.target.value)
                            }
                            placeholder="customer@example.com"
                        />
                    </div>

                    <div className="grid-2">
                        <div className="form-field">
                            <label className="form-label" htmlFor="support-category">
                                Category
                            </label>
                            <select
                                id="support-category"
                                className="select"
                                value={newTicket.category}
                                onChange={(event) =>
                                    updateNewTicket('category', event.target.value)
                                }
                            >
                                <option value="account_billing">Account / billing</option>
                                <option value="bug_report">Bug report</option>
                                <option value="feature_request">Feature request</option>
                                <option value="how_to">How-to</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div className="form-field">
                            <label className="form-label" htmlFor="support-channel">
                                Intake channel
                            </label>
                            <select
                                id="support-channel"
                                className="select"
                                value={newTicket.channel}
                                onChange={(event) => updateNewTicket('channel', event.target.value)}
                            >
                                <option value="email">Email</option>
                                <option value="chat">Chat</option>
                                <option value="help_ai">Help AI</option>
                                <option value="sms">SMS</option>
                                <option value="phone">Phone</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-field">
                        <label className="form-label" htmlFor="support-summary">
                            Subject
                        </label>
                        <input
                            id="support-summary"
                            className="form-input"
                            type="text"
                            value={newTicket.summary}
                            onChange={(event) => updateNewTicket('summary', event.target.value)}
                            placeholder="Short customer-visible issue summary"
                        />
                    </div>

                    <div className="form-field">
                        <label className="form-label" htmlFor="support-message">
                            First message
                        </label>
                        <textarea
                            id="support-message"
                            className="form-textarea"
                            rows={6}
                            value={newTicket.user_message}
                            onChange={(event) =>
                                updateNewTicket('user_message', event.target.value)
                            }
                            placeholder="Paste the customer message or write the intake note."
                        />
                    </div>

                    <p className="text-muted" style={{ fontSize: 12 }}>
                        The ticket is stored in the existing support_tickets table and the intake
                        channel is logged to the event stream.
                    </p>
                </div>
            </Modal>
        </div>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
